(function(angular, _) {
    'use strict';

    angular.module('wtApp.dataService')
        .factory('WtAnalysisColumns', ['$injector', function($injector) {
            const Monads = $injector.get('Monads');
            const WT_CONST = $injector.get('WT_CONST');
            const Columns = $injector.get('Columns');
            const analysisColumnUtils = $injector.get('analysisColumnUtils');
            const QueryLimits = $injector.get('QueryLimits');
            const Filters = $injector.get('Filters');
            const accountService = $injector.get('accountService');
            const WtObservable = $injector.get('WtObservable');
            const ConfigPeriodColumn = $injector.get('ConfigPeriodColumn');
            const featureFlagService = $injector.get('featureFlagService');

            const SORT_ASCENDING = 'ASCENDING';
            const SORT_DESCENDING = 'DESCENDING';

            /**
             * @ngdoc service
             * @name WtAnalysisColumns
             * @description Factory responsible for the analysis columns management
             */
            return class WtAnalysisColumns {
                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns.visualizationProps
                 * @description The list of column properties that deal with table visualizations
                 * @returns {Array}
                 * @static
                 */
                static get visualizationProps() {
                    return [
                        'dataVisual',
                        'dataVisualTarget',
                        'dataVisualTargetMin',
                        'dataVisualTargetMax',
                        'dataVisualNormalized',
                        'dataVisualShowSparklineHeader',
                        'sumValue'
                    ];
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysis.ACTIONS
                 * @description A map of actions that can be emitted by the column "changes" observable
                 * @returns {Object}
                 * @static
                 */
                static get ACTIONS() {
                    return {
                        /**
                         * Generic event informing about any type of change performed on the columns configuration
                         */
                        COLUMNS_UPDATE: 'COLUMNS_UPDATE',

                        /**
                         * Triggered whenever the visualization of a column has changed
                         */
                        VISUALIZATION_CHANGE: 'VISUALIZATION_CHANGE',

                        /**
                         * Dimension columns order changed
                         */
                        DIMENSION_COLUMNS_REORDERED: 'DIMENSION_COLUMNS_REORDERED',

                        /**
                         * Metric columns order changed
                         */
                        METRIC_COLUMNS_REORDERED: 'METRIC_COLUMNS_REORDERED',

                        /**
                         * New column added
                         */
                        COLUMN_ADDED: 'COLUMN_ADDED',

                        /**
                         * Existing column removed
                         */
                        COLUMN_REMOVED: 'COLUMN_REMOVED',

                        /**
                         * Session limit updated
                         */
                        SESSION_LIMIT_UPDATED: 'SESSION_LIMIT_UPDATED',

                        /**
                         * Column limit updated
                         */
                        COLUMN_LIMIT_UPDATED: 'COLUMN_LIMIT_UPDATED',

                        /**
                         * Columns sorting updated
                         */
                        SORTING_UPDATED: 'SORTING_UPDATED',

                        /**
                         * Chart visibility configuration changed
                         */
                        COLUMN_IN_CHART_TOGGLED: 'COLUMN_IN_CHART_TOGGLED',

                        /**
                         * Triggered when time series column is toggled.
                         */
                        TIME_SERIES_COLUMN_TOGGLE: 'TIME_SERIES_COLUMN_TOGGLE',

                        /**
                         * Triggered when the configuration of a metric is being updated.
                         */
                        METRIC_COLUMN_UPDATE: 'METRIC_COLUMN_UPDATE'
                    };
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#pristineConfig
                 * @description Exposes the pristine columns configuration object to the outer world. Contains the complete column configuration,
                 * ie. the analysis column merged with the arrange type definition
                 * @returns {Array}
                 */
                get pristineConfig() {
                    return this.pristineData_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#config
                 * @description Exposes the columns configurations array to the outer world. Contains the complete column configuration,
                 * ie. the analysis column merged with the arrange type definition
                 * @returns {Array}
                 */
                get config() {
                    return this.data_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#analysis
                 * @description Retrieves the analysis the columns belong to
                 * @returns {WtAnalysis}
                 */
                get analysis() {
                    return this.analysis_;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#isRealTime
                 * @description Is the analysis a live analysis
                 * @returns {boolean}
                 */
                get isRealTime() {
                    return this.analysis_.get('realTime');
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#layer
                 * @description The layer the columns operate on (the regular analyses and live analyses work on separate column definitions)
                 * @returns {string}
                 */
                get layer() {
                    return this.analysis.layer;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#actions
                 * @description Exposes the possible actions map to be used by the columns changes consumers
                 * @returns {Object}
                 */
                get actions() {
                    return WtAnalysisColumns.ACTIONS;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#hasDimension
                 * @description  Returns true if analysis has valid dimension
                 * @returns {boolean}
                 */
                get hasDimension() {
                    return _.size(this.getDimensions()) > 0;
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#maxRows
                 * @description  Returns the maximum upperLimist that can be set on a column
                 * @returns {number}
                 */
                get maxRows() {
                    return _.get(WT_CONST, 'TABLE.MAX_ROWS');
                }

                /**
                 * @ngdoc property
                 * @name WtAnalysisColumns#reloadOnSort_
                 * @description Determines if the change of the sorting will reload the analysis or not.
                 * @returns {boolean}
                 */
                get reloadOnSort_() {
                    return !(this.analysis.isTimeSeriesViewMode() && this.analysis.getEditState() && this.analysis.get('viewMode.timeSeries.viewType') === 'top');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#constructor
                 * @description WtAnalysisColumns constructor
                 * @param {Array} config The analysis columns array
                 * @param {WtAnalysis} analysis The analysis instance the columns belong to
                 * @throws {Error} Invalid argument exception
                 */
                constructor(config, analysis) {
                    // Circular dependency
                    this.WtAnalysis = $injector.get('WtAnalysis');

                    if (!_.isArray(config) || !_.size(config)) {
                        throw Error(`WtAnalysisColumns expecting an array of column definitions, got ${typeof config}`);
                    }

                    if (!(analysis instanceof this.WtAnalysis)) {
                        throw Error('WtAnalysisColumns expecting an instance of WtAnalysis');
                    }

                    /**
                     * The analysis instance whose columns are handled by the WtAnalysisColumns
                     * @type {WtAnalysis}
                     * @private
                     */
                    this.analysis_ = analysis;

                    // Filters analysis column properties, merges with the arrange type definition and defaults values that do not exist
                    const columns = this.getCompleteConfiguration_(config);

                    /**
                     * Holds the original data
                     * @type {Array}
                     * @private
                     */
                    this.pristineData_ = _.cloneDeep(columns);

                    /**
                     * Holds the current data
                     * @type {Array}
                     * @private
                     */
                    this.data_ = _.cloneDeep(columns);

                    /**
                     * Map of upper and lower limits per dimension per analysis variant
                     * There are two kinds of limits, the ones that are persisted on the columns configuration which are configurable by the
                     * end user and the others that are enforced by the application in the current session based on the analysis configuration
                     * e.g. analysis variant or dimension type.
                     * The current structure stores the session limits that are not persisted on the columns configuration
                     * @type {Object}
                     * @example {PIVOT_AS_LIST: [1, 20]}
                     * @private
                     */
                    this.sessionLimits_ = {};

                    /**
                     * The observer object that can be used to push changes into the observable
                     * @example
                     *  - Notify the listeners about a new value: this.changesEmitter_.next('value');
                     *  - Notify the listeners about an error: this.changesEmitter_.error(new Error('Something went wrong'))
                     *  - Notify the listeners about the completion: this.changesEmitter_.complete()
                     * @type {null|Object}
                     * @private
                     */
                    this.changesEmitter_ = null;

                    /**
                     * The observable object that can be subscribed to listen to the changes on the columns instance
                     * @example
                     *  const subscription = columns.changes.subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the column change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @type {WtObservable}
                     */
                    this.changes = new WtObservable((observer) => {
                        this.changesEmitter_ = observer;
                    });

                    /**
                     * Enhances the columns.changes observable by adding a filter to be able to subscribe to specific action(s)
                     * @param {string|Array} actionType One or more action types to subscribe to
                     * @example
                     *  const subscription = columns.changesOfType('PROPERTY_CHANGE').subscribe((action) => {
                     *      const type = action.type;
                     *      const payload = action.payload;
                     *      // React to the analysis columns change
                     *  });
                     *
                     *  // Cleanup method should be invoked when the listener is destroyed or does not need the updates
                     *  subscription.unsubscribe();
                     * @returns {WtObservable}
                     */
                    this.changesOfType = function(actionType) {
                        return this.changes
                            .filter((action) => _.includes(_.isArray(actionType) ? actionType : [actionType], _.get(action, 'type')));
                    };

                    this.init_();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#init_
                 * @description Initialization phase of the instance
                 * @returns {WtAnalysisColumns}
                 */
                init_() {
                    // Filter out all the columns that are based on unavailable arrange types (deleted custom metrics|formulas)
                    this.data_ = _.filter(this.data_, (column) => Monads.layer(this.layer).monad(column.arrangeType));

                    return this.initChartVisibility_().initSparklineVisuals_();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#initChartProperties_
                 * @description Sets chart properties to defaults if none are specified in metric columns
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                initChartProperties_() {
                    _.chain(this.getMetrics())
                        .forEach(metric => _.defaults(metric, {chart: {visible: false, yAxis: 0}}))
                        .value();
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#initChartVisibility_
                 * @description If no metric is set to be visible in the chart the first one will be forced to be visible.
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                initChartVisibility_() {
                    if (this.isRealTime) {
                        return this;
                    }

                    this.initChartProperties_();

                    const hasVisibleColumns = _.chain(this.getMetrics())
                        .filter({chart: {visible: true}})
                        .size()
                        .gt(0)
                        .value();

                    if (hasVisibleColumns) {
                        return this;
                    }

                    const chartVisibility = _.chain(this.getMetrics())
                        .first()
                        .pick(['alias', 'chart'])
                        .extend({chart: {visible: true, yAxis: 0}})
                        .value();

                    return this.setChartVisibility(chartVisibility);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#initSparklineVisuals_
                 * @description Will init real time sparkline columns to the new config
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                initSparklineVisuals_() {
                    if (!this.isRealTime) {
                        return this;
                    }

                    _.chain(this.getMetrics())
                        .filter(({sumValue, dataVisual}) => !sumValue && !_.includes(['RANK', 'SPARKLINE'], dataVisual))
                        .forEach(column => {
                            const dataVisual = 'SPARKLINE';
                            const dataVisualNormalized = false;
                            const dataVisualShowSparklineHeader = true;
                            _.merge(column, {dataVisual, dataVisualNormalized, dataVisualShowSparklineHeader});
                        })
                        .value();

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#toJSON
                 * @description Converts the instance into a serializable object. Prepares the column definitions to be persisted.
                 * @returns {Object}
                 */
                toJSON() {
                    return analysisColumnUtils.getEssentialColumnList(this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#toString
                 * @description Stringify the instance
                 * @returns {string}
                 */
                toString() {
                    return JSON.stringify(
                        this.toJSON()
                    );
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#isPristine
                 * @description Checks if the columns configuration was changed since it was initialized
                 * @returns {boolean}
                 */
                isPristine() {
                    return _.isEqual(this.pristineData_, this.data_);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#dispatchAction
                 * @description Emits an event in the columns changes observable
                 * @param {string} type The event name
                 * @param {Object=} payload The payload of the event
                 * @returns {WtAnalysisColumns}
                 */
                dispatchAction(type, payload = {}) {
                    if (type && _.includes(_.values(this.actions), type)) {
                        this.changesEmitter_.next({type, payload});
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getColumn
                 * @description Retrieves the column from the columns list by alias or column configuration
                 * @param {string|Object} iColumn The column alias or a column configuration with alias
                 * @returns {Object|undefined} The found column or undefined if no column found
                 */
                getColumn(iColumn) {
                    const alias = _.isString(iColumn) ? iColumn : _.get(iColumn, 'alias', '');

                    return _.find(this.config, {alias});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getRealColumn
                 * @description Retrieves the real column from the list of the analysis columns based on a column(real or derived) alias or configuration
                 * @example Retrieve the real column based on a time-series replicated column
                 * @param {string|Object} iColumn The column alias or a column configuration with alias
                 * @returns {Object|undefined} The found column or undefined if no column found
                 */
                getRealColumn(iColumn) {
                    let alias = _.isString(iColumn) ? iColumn : _.get(iColumn, 'alias', '');

                    if (this.analysis.isTimeSeriesViewMode()) {
                        alias = this.analysis.getTimeSeriesParentAlias(alias);
                    } else if (this.analysis.isComparisonViewMode()) {
                        alias = this.analysis.getComparisonParentAlias(alias);
                    }

                    return _.find(this.config, {alias});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getColumnIndex
                 * @description Retrieves the column index from the columns list by alias or column configuration
                 * @param {string|Object} iColumn The column alias or a column configuration with alias
                 * @returns {number} the index of the column
                 */
                getColumnIndex(iColumn) {
                    const column = this.getColumn(iColumn);
                    return _.findIndex(this.config, {alias: _.get(column, 'alias')});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getAliases
                 * @description Retrieves the columns aliases
                 * @returns {Array}
                 */
                getAliases() {
                    return _.map(this.config, 'alias');
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getDimensions
                 * @description Retrieves the columns of type dimension
                 * @returns {Array}
                 */
                getDimensions() {
                    return _.filter(this.config, {dimension: true});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getFirstDimension
                 * @description Retrieves the first column of type dimension
                 * @returns {Object}
                 * @throws Error
                 */
                getFirstDimension() {
                    const dimension = _.first(this.getDimensions());

                    if (!dimension) {
                        throw Error(`The analysis ${this.analysis.get('title')} is missing dimension columns`);
                    }

                    return dimension;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getMetrics
                 * @description Retrieves the columns of type metric
                 * @returns {Array}
                 */
                getMetrics() {
                    return _.filter(this.config, {metric: true});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#addDimension_
                 * @description Adds a new dimension to the columns list. Returns a clone of the current columns list with the added dimension
                 * @param {Object} iColumn The dimension column to be added
                 * @returns {Array} The new columns configuration
                 * @throws Error
                 * @private
                 */
                addDimension_(iColumn) {
                    // Validate max dimensions that are allowed to be added
                    if (this.getAllowedDimensionsQuota(iColumn) <= 0) {
                        throw Error('Maximum allowed dimension quota exceeded');
                    }

                    // Validates the category limit
                    if (this.isCategoriesLimitExceeded(iColumn)) {
                        throw Error('Maximum dimension categories quota exceeded');
                    }

                    const column = _.cloneDeep(iColumn);
                    const columns = _.cloneDeep(this.config);
                    const columnsByType = analysisColumnUtils.splitColumnsByElementType(columns, this.layer);

                    column.alias = this.computeNextAlias(column);

                    columnsByType.dimensions.push(column);

                    return columnsByType.dimensions.concat(columnsByType.metrics);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#replaceDimension
                 * @description Replaces the current dimension with a new dimension
                 * @param   {string} aliasToReplace The alias of the column to replace
                 * @param   {string} arrangeType The arrangeType of the new dimension
                 * @param   {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysisColumns}
                 * @throws Error
                 */
                replaceDimension(aliasToReplace, arrangeType, reload = true) {
                    const alias = this.computeNextAlias(arrangeType);

                    const column = analysisColumnUtils.getEssentialColumnConfiguration(alias, this.layer, false);

                    const oldColumn = this.getColumn(aliasToReplace);

                    if (!column.dimension || !oldColumn.dimension) {
                        throw Error('Failed to replace dimension due to an invalid dimension configuration');
                    }

                    _.chain(column)
                        .set('lowerLimit', _.get(oldColumn, 'lowerLimit'))
                        .set('upperLimit', _.get(oldColumn, 'upperLimit'))
                        .value();

                    const atIndex = this.getColumnIndex(oldColumn);

                    const columns = _.cloneDeep(this.config);

                    columns.splice(atIndex, 1, column);

                    this.analysis.resetTimeSeriesValues();

                    return this
                        .updateData_(columns, false)
                        .dispatchAction('COLUMN_ADDED', {column, reload});
                }

                getDuplicationAlias(column_) {
                    const column = this.getColumn(column_);
                    return this.computeNextAlias(column);
                }

                getDuplicationTitle(column_, titlePostfix) {
                    const column = this.getColumn(column_);
                    return `${_.get(column, 'title')} (${titlePostfix})`;
                }

                getDuplicationIndex_(column_) {
                    const column = this.getColumn(column_);
                    const alias = _.get(column, 'alias');
                    return _.findIndex(this.config, {alias}) + 1;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#duplicateMetric
                 * @description Adds a replica of an existing metric column to the column configurations list
                 * @param   {Object|string} column_ The entire metric configuration or its alias
                 * @param   {string} titlePostfix A little string post fix to alternate the original metric title
                 * @param   {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {Array} The new metrics list
                 * @private
                 */
                duplicateMetric(column_, titlePostfix, reload = true) {
                    const column = this.getColumn(column_);

                    if (!column) {
                        throw Error(`Failed to find and thus duplicate metric with alias ${JSON.stringify(column_)}.`);
                    }

                    const alias = this.getDuplicationAlias(column_);
                    const title = this.getDuplicationTitle(column_, titlePostfix);
                    const inTimeSeries = false;
                    const duplicateColumn = _.chain(column)
                        .cloneDeep()
                        .merge({alias, title, inTimeSeries})
                        .value();
                    _.unset(duplicateColumn, 'sortDirection');
                    const atIndex = this.getDuplicationIndex_(column_);
                    return this.addColumn(duplicateColumn, reload, atIndex);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#addMetric_
                 * @description Adds a new metric to the column configurations list
                 * @param {Object} iColumn The metric configuration
                 * @param {number=} atIndex_ The desired index at which to inject the metric at
                 * @returns {Array} The new metrics list
                 * @private
                 */
                addMetric_(iColumn, atIndex_ = null) {
                    if (this.getAllowedMetricsQuota() <= 0) {
                        throw Error('Maximum allowed metrics quota exceeded');
                    }

                    const column = _.cloneDeep(iColumn);
                    const columns = _.cloneDeep(this.config);

                    // Special handling of live metrics
                    if (this.isRealTime) {
                        column.sumValue = true;
                    }

                    const atIndex = _.isInteger(atIndex_) && atIndex_ >= 0 ? atIndex_ : columns.length;
                    columns.splice(atIndex, 0, column);

                    return columns;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#addColumn
                 * @description Adds a new column to the analysis.
                 * For dimensions will automatically detect if the dimension should be replaced instead of added.
                 * @param {Object} iColumn The column configuration
                 * @param {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysisColumns}
                 * @throws Error
                 */
                addColumn(iColumn, reload = true, atIndex = null) {
                    if (!_.isPlainObject(iColumn)) {
                        throw Error('Failed to add a new column due to an invalid column configuration');
                    }

                    let columns = this.config;
                    const column = this.getCompleteConfiguration_(iColumn);

                    if (column.dimension) {
                        columns = this.addDimension_(column);
                    } else {
                        columns = this.addMetric_(column, atIndex);
                    }

                    return this
                        .updateData_(columns, false)
                        .dispatchAction('COLUMN_ADDED', {column, reload});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#updateMetricColumn
                 * @description Updates a metric column configuration in the columns list
                 * @param {Object} column The column configuration to be updated
                 * @param standAlone determines whether the analsis is in stand alone.
                 * @returns {WtAnalysisColumns}
                 */
                updateMetricColumn(column, standAlone) {
                    return this
                        .updateColumn(column, false)
                        .dispatchAction('METRIC_COLUMN_UPDATE', {reload: true, standAlone});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#updateColumn
                 * @description Updates a column configuration in the columns list
                 * @param {Object} iColumn The column configuration to be updated
                 * @param {boolean=} reload Do the changes require to reload the data
                 * @returns {WtAnalysisColumns}
                 * @throws {Error}
                 * @todo: Turn to private && merge with updateColumn_
                 */
                updateColumn(iColumn, reload = true) {
                    const column = this.getCompleteConfiguration_(iColumn);

                    const columns = this.updateColumn_(column);

                    return this.updateData_(columns, reload);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#updateColumn_
                 * @description Updates the column definition of an existing column in the columns list
                 * @param {Object} column The new column configuration
                 * @returns {Array} A new columns list containing the updated column configuration
                 * @private
                 */
                updateColumn_(column) {
                    const columns = _.cloneDeep(this.config);
                    const columnIndex = _.findIndex(columns, {alias: column.alias});

                    if (columnIndex >= 0) {
                        columns[columnIndex] = _.cloneDeep(column);
                    }

                    return columns;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#deleteColumn
                 * @description Removes the column from the columns configuration
                 * @param {Object|string} iColumn The column configuration or the column alias to be removed
                 * @param {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysisColumns}
                 * @throws {Error}
                 */
                deleteColumn(iColumn, reload = true) {
                    const column = this.getColumn(iColumn);

                    if (!column) {
                        throw Error('The requested column to be deleted is not part of the columns list');
                    }

                    if (!this.canDeleteColumn_(column)) {
                        throw Error(`Failed to remove the column with title ${column.title}`);
                    }

                    const columns = this.deleteColumn_(column);

                    return this
                        .updateData_(columns, false)
                        .dispatchAction('COLUMN_REMOVED', {column, reload});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#canDeleteColumn_
                 * @description Performs vital checks for a column before being deleted. Does not contain UI business logic like check
                 * for locked columns (yet)
                 * @param {Object} column The column to be checked
                 * @returns {boolean}
                 * @private
                 */
                canDeleteColumn_(column) {
                    const siblingColumns = column.dimension ? this.getDimensions() : this.getMetrics();

                    return _.size(siblingColumns) > 1;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#deleteColumn_
                 * @description Removes a column from the the columns list
                 * @param {Object} column The column to be removed
                 * @returns {Array} The new columns configuration
                 * @private
                 */
                deleteColumn_(column) {
                    const columns = _.cloneDeep(this.config);
                    const columnIndex = _.findIndex(columns, {alias: column.alias});

                    if (columnIndex < 0) {
                        return columns;
                    }

                    columns.splice(columnIndex, 1);

                    // Force the visible metric if last visible was removed
                    if (!_.find(columns, (col) => _.get(col, 'chart.visible', false))) {
                        const firstMetric = _.find(columns, {metric: true});

                        if (firstMetric) {
                            firstMetric.chart = _.get(firstMetric, 'chart', {visible: true, yAxis: 0});
                            firstMetric.chart.visible = true;
                        }
                    }

                    return columns;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#updateData_
                 * @description Updates the column configurations
                 * @param {Array} columns The new column configurations list
                 * @param {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                updateData_(columns, reload = false) {
                    const previousValue = this.data_;
                    const currentValue = columns;

                    if (!_.isArray(columns) || !_.size(columns)) {
                        return this.data_; // Candidate for exception
                    }

                    this.data_ = columns;

                    this.dispatchAction('COLUMNS_UPDATE', {reload, currentValue, previousValue});

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#cacheSessionLimits_
                 * @description Caches a set of given limits for the given variant.
                 * @param   {string} variant
                 * @param   {number} lowerLimit
                 * @param   {number} upperLimit
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                cacheSessionLimits_(variant, lowerLimit, upperLimit) {
                    this.sessionLimits_[variant] = [lowerLimit, upperLimit];
                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#destroy
                 * @description Method invoked before the instance is destroyed to release the resources and perform the cleanup.
                 */
                destroy() {
                    if (this.changesEmitter_) {
                        this.changesEmitter_.complete();
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#goToFirstPage
                 * @description Resets the limits of the first dimension to navigate to the first page of the analysis data
                 * @param   {boolean=} reload Flag denoting that the changes require the analysis data to be reloaded
                 * @returns {WtAnalysisColumns}
                 */
                goToFirstPage(reload = true) {
                    Columns.resetLimit(this.config, this.layer);

                    // Notify the interested parties about the change
                    this.updateData_(this.config, reload);

                    const dimension = this.getFirstDimension();

                    if (dimension) {
                        this.cacheSessionLimits_(this.analysis.get('variant'), dimension.lowerLimit, dimension.upperLimit);
                    }

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#computeNextAlias
                 * @description Calculates the alias for a column based on the current analysis columns configuration
                 * @param {Object|string} iColumn The column configuration or the arrangeType
                 * @returns {string} The possible alias for the column if it is to be added to the analysis
                 */
                computeNextAlias(iColumn) {
                    const arrangeType = _.isString(iColumn) ? iColumn : _.get(iColumn, 'arrangeType', '');

                    const index = _.reduce(this.config, (acc, column) => {
                        if (column.arrangeType !== arrangeType) {
                            return acc;
                        }

                        const columnIndex = parseInt(column.alias.split('|').pop()) || 0;

                        return (acc <= columnIndex) ? columnIndex + 1 : acc;
                    }, 0);

                    return `${arrangeType}|${index}`;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getCompleteConfiguration_
                 * @description Retrieves the complete column/columns configuration, i.e. merged with the arrange type definition with normalized/default properties
                 * @param {Array|Object} iColumns The column definitions list or a column definition. The column definition can also be only the arrange type.
                 * @returns {Array|Object} Returns the complete column definitions list or the column list based on the input argument
                 * @private
                 */
                getCompleteConfiguration_(iColumns) {
                    const columns = _.map(_.isArray(iColumns) ? iColumns : [iColumns], (column) => {
                        // Create new column configuration when only arrange type is known
                        if (_.isString(column)) {
                            return analysisColumnUtils.getEssentialColumnConfiguration(column);
                        }

                        return column;
                    });

                    const completeConfiguration = analysisColumnUtils.getCompleteColumnConfigurations(
                        Columns.getAnalysisConfigurationColumnsWithAliases(columns),
                        false,
                        this.layer
                    );

                    return _.isArray(iColumns) ? completeConfiguration : completeConfiguration[0];
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#requiresReplaceDimension_
                 * @description Performs a check on the analysis to identify if the current configuration prohibits
                 * new dimensions addition enforcing dimension replacement
                 * @param   {Object|string} column Either the entire column configuration or the arrange type
                 * @returns {boolean}
                 * @private
                 * @todo: SWAT-3764 Can we drop or at least rename...
                 */
                requiresReplaceDimension_(column) {
                    if (this.isRealTime) {
                        return true;
                    }

                    if (this.analysis.isTimeSeriesViewMode()) {
                        return true;
                    }

                    if (!this.analysis.canHaveManyDimensions) {
                        return true;
                    }

                    if (this.analysis.isComparisonViewMode() && !featureFlagService.getFeatureFlag('comparisonPivotEnabled')) {
                        return true;
                    }

                    const arrangeType = _.isString(column) ? column : _.get(column, 'arrangeType', '');

                    return !Monads.layer(this.analysis.layer).isPivotUsable(arrangeType);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getAllowedDimensionsQuota
                 * @description Returns the number of dimensions that can still be added to the analysis
                 * @param   {Object|string} column Either the entire column configuration or the arrange type
                 * @returns {number}
                 */
                getAllowedDimensionsQuota(column) {
                    const currentCount = _.size(this.getDimensions());
                    const maxCount = accountService.getAnalysisPivotDimensionsQuota();

                    if (this.requiresReplaceDimension_(column)) {
                        return 0;
                    }

                    // TODO SWAT-1312 Validate if the analysis allows addition of new dimensions
                    return (maxCount - currentCount);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getAllowedMetricsQuota
                 * @description Returns the number of metrics that can still be added to the analysis
                 * @returns {number}
                 */
                getAllowedMetricsQuota() {
                    const currentCount = _.size(this.getMetrics());

                    const maxCount = this.analysis.isComparisonViewMode() ? accountService.getMaxComparisonMetrics() : accountService.getAnalysisMetricsQuota();

                    // TODO SWAT-1312 Validate if the analysis allows addition of new metrics, although might not be needed.
                    return (maxCount - currentCount);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getDimensionCategories
                 * @description Retrieves a list of unique categories for all the columns of type dimensions
                 * @returns {Array}
                 * @private
                 */
                getDimensionCategories() {
                    return _.chain(this.getDimensions())
                        .map((col) => col.categoryOf || col.arrangeType)
                        .uniq()
                        .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#isCategoriesLimitExceeded
                 * @description Checks if a dimension column does not exceed the limit of categories analysis dimensions can have
                 * @param {Object} iColumn The dimension to be checked for category limit
                 * @returns {boolean}
                 */
                isCategoriesLimitExceeded(iColumn) {
                    const column = this.getCompleteConfiguration_(iColumn);
                    const categories = this.getDimensionCategories();
                    const maxCategoriesQuota = accountService.getAnalysisDistinctPivotDimensionsQuota();
                    const columnCategory = _.isEmpty(column.categoryOf) ? column.arrangeType : column.categoryOf;
                    const categoryExits = _.includes(categories, columnCategory);

                    // The categories limit is not exceeded. A dimension with a new category can be added.
                    if (_.size(categories) < maxCategoriesQuota) {
                        return false;
                    }

                    // The categories limit was reached. Only dimensions with categories already existing in the analysis can be added
                    return !categoryExits;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setColumnProperties_
                 * @description Updates a columns properties with values as indicated in the
                 * props hash.
                 * @param {string} alias The column alias
                 * @param {Object} props Key/value hash with props to update
                 * @param {boolean=} reload Do the changes require data reload
                 * @returns {WtAnalysisColumns}
                 * @private
                 */
                setColumnProperties_(alias, props, reload = false) {
                    const column = _.cloneDeep(this.getColumn(alias));

                    if (!column) {
                        return this;
                    }

                    _.forEach(props, (value, key) => {
                        _.set(column, key, value);
                    });

                    this.updateColumn(column, reload);

                    return this;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#hasSumValueUpdate_
                 * @description Helper method for setVisualization. Will determine if the column change should trigger
                 * a reload or not.
                 * @param {Object} visualsConfig Object visual props to update
                 * @returns {boolean}
                 * @private
                 */
                hasSumValueUpdate_(visualsConfig) {
                    const alias = _.get(visualsConfig, 'alias');
                    const column = this.getColumn(alias);
                    const oldSumValue = _.get(column, 'sumValue', this.analysis.defaultSumValue);
                    const newSumValue = _.get(visualsConfig, 'sumValue', this.analysis.defaultSumValue);
                    return oldSumValue !== newSumValue;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setVisualization
                 * @description Updates a columns visualization configuration
                 * @param {Object} visualsConfig Object visual props to update and the alias
                 * @returns {WtAnalysisColumns}
                 */
                setVisualization(visualsConfig) {
                    const alias = _.get(visualsConfig, 'alias');
                    const visuals = _.pick(visualsConfig, WtAnalysisColumns.visualizationProps);
                    const reload = this.hasSumValueUpdate_(visualsConfig);
                    const column = this.getColumn(alias);

                    this.setColumnProperties_(alias, visuals, reload);

                    return this.dispatchAction('VISUALIZATION_CHANGE', {column});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setChartVisibility
                 * @description Updates a columns chart configuration
                 * @param {Object} chartVisibilityConfig Object with chart props to update and the alias
                 * @returns {WtAnalysisColumns}
                 */
                setChartVisibility(chartVisibilityConfig) {
                    const alias = _.get(chartVisibilityConfig, 'alias');
                    const chartVisibility = _.pick(chartVisibilityConfig, 'chart');

                    if (!this.getColumn(alias)) {
                        return this;
                    }

                    this.setColumnProperties_(alias, chartVisibility, false);

                    const column = this.getColumn(alias);

                    return this.dispatchAction('COLUMN_IN_CHART_TOGGLED', {column});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setTimeDimensionLimit
                 * @description Will reset the limits of first dimension if it is time related and given a timeFilter
                 * @param   {Object} timeFilter A time filter object
                 * @param   {boolean} reload Change triggers a reload or not
                 * @returns {WtAnalysisColumns}
                 */
                setTimeDimensionLimit(timeFilter, reload = true) {
                    if (!this.hasDimension) {
                        return this;
                    }

                    const firstDimension = this.getFirstDimension();
                    const timeFormat = firstDimension.timeFormat;
                    const upperLimit = QueryLimits.getUpperLimitByTimeFormat(timeFormat, timeFilter);

                    if (this.analysis.get('variant') === 'PIVOT_AS_LIST' || !timeFormat || !upperLimit) {
                        return this;
                    }

                    return this.setColumnLimits(firstDimension, 1, _.min([this.maxRows, upperLimit]), reload);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#updateTimeSeriesTopSorting
                 * @description Iterates over the columns list and updates the sortDirection of the columns if the analysis is in timeSeries.
                 * Select top X requires sorting by a metric and metrics that are not included in the time series should not participate in ordering
                 * @returns {boolean} Indicates if there was any update performed on columns sortDirection flag
                 */
                updateTimeSeriesTopSorting() {
                    const columns = this.config;

                    let sortDirectionChanged = false;

                    // When a time series metric is toggled, the ordering should be updated in case the view type is top as the top time series
                    // requires an ordering column to compute the top.
                    // If the metric is disabled from time series - remove it from ordering
                    // If no time series metric is left for ordering, use the first in time series metric for ordering
                    if (this.analysis.isTimeSeriesViewMode() && this.analysis.get('viewMode.timeSeries.viewType') === 'top') {
                        _.forEach(columns, (iColumn) => {
                            if (iColumn.sortDirection && !iColumn.inTimeSeries) {
                                delete iColumn.sortDirection;
                                sortDirectionChanged = true;
                            }
                        });

                        const hasInTimeSeriesOrdering = _.find(columns, (iColumn) => {
                            return Boolean(iColumn.sortDirection && iColumn.inTimeSeries);
                        });

                        if (!hasInTimeSeriesOrdering) {
                            const firstInTimeSeriesColumn = _.find(columns, {
                                inTimeSeries: true
                            });

                            if (firstInTimeSeriesColumn) {
                                firstInTimeSeriesColumn.sortDirection = SORT_DESCENDING;
                                sortDirectionChanged = true;
                            }
                        }
                    }

                    // Notify interested parties about the change in the columns
                    if (sortDirectionChanged) {
                        this.updateData_(columns);
                    }

                    return sortDirectionChanged;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#toggleTimeSeriesColumn
                 * @description Toggles inTimeSeries flag of a column
                 * @param {Object|string} iColumn The column alias or configuration
                 * @returns {boolean}
                 */
                toggleTimeSeriesColumn(iColumn) {
                    const column = this.getColumn(iColumn);
                    const hasTimeSeriesColumns = this.analysis.getTimeSeriesColumns().length > 0;
                    const timeSeriesLimitReached = this.analysis.isTimeSeriesLimitReached();

                    if (!column || (timeSeriesLimitReached && hasTimeSeriesColumns && !column.inTimeSeries)) {
                        return false;
                    }

                    column.inTimeSeries = !column.inTimeSeries;

                    this.updateTimeSeriesTopSorting();

                    this.updateData_(this.config, false);

                    this.dispatchAction('TIME_SERIES_COLUMN_TOGGLE', {column});

                    this.ensureChartVisibility_();

                    return column.inTimeSeries;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#ensureChartVisibility
                 * @description Sets the chart visibility to true for the first time series columns if there is no metric at chart
                 * @private
                 */
                ensureChartVisibility_() {
                    const timeSeriesColumns = this.analysis.getTimeSeriesColumns();
                    const columnsInChart = _.chain(timeSeriesColumns).some((col) => _.get(col, 'chart.visible')).value();
                    if (!columnsInChart) {
                        const chartVisibility = _.chain(timeSeriesColumns)
                            .first()
                            .pick(['alias', 'chart'])
                            .extend({chart: {visible: true, yAxis: 0}})
                            .value();

                        this.setChartVisibility(chartVisibility);
                    }
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setColumnsOrder_
                 * @description Returns current column list with changed order
                 * @param   {Array} iColumnsOrder An array of column aliases or column configurations
                 * @returns {Array<Object>}
                 * @private
                 */
                setColumnsOrder_(iColumnsOrder) {
                    return _.chain(iColumnsOrder)
                        .map((column) => this.getColumn(column))
                        .filter((column) => column)
                        .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#hasOrderChange_
                 * @description Determines if a list of aliases has a different order then a list of column aliases.
                 * Detects invalid aliasOrder lists - i.e. with aliases that are not found in the list of columns.
                 * @param   {Array<Object>} columns An array of column configurations
                 * @param   {Array<string>} aliasOrder An array of column aliases
                 * @returns {boolean}
                 * @throws {Error}
                 * @private
                 */
                hasOrderChange_(columns, aliasOrder) {
                    const aliases = _.map(columns, 'alias');

                    const hasAliasMismatch = _.chain(aliases)
                        .sortBy()
                        .isEqual(_.sortBy(aliasOrder))
                        .isEqual(false)
                        .value();

                    if (hasAliasMismatch) {
                        throw Error('Alias order items differ from actual column aliases.');
                    }

                    return _.chain(aliases).isEqual(aliasOrder).isEqual(false).value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setMetricOrder
                 * @description Returns current column list with changed order
                 * @param   {Array} metricOrder An array of metric column aliases
                 * @returns {WtAnalysisColumns}
                 */
                setMetricOrder(metricOrder) {
                    if (!this.hasOrderChange_(this.getMetrics(), metricOrder)) {
                        return this;
                    }

                    const columns = this.setColumnsOrder_(_.map(this.getDimensions(), 'alias').concat(metricOrder));

                    return this
                        .updateData_(columns, false)
                        .dispatchAction('METRIC_COLUMNS_REORDERED', {columns});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setDimensionOrder
                 * @description Returns current column list with changed order
                 * @param   {Array} dimensionOrder An array of dimension column aliases
                 * @returns {WtAnalysisColumns}
                 */
                setDimensionOrder(dimensionOrder) {
                    if (!this.hasOrderChange_(this.getDimensions(), dimensionOrder)) {
                        return this;
                    }

                    const columns = this.setColumnsOrder_(dimensionOrder.concat(_.map(this.getMetrics(), 'alias')));

                    return this
                        .updateData_(columns, false)
                        .dispatchAction('DIMENSION_COLUMNS_REORDERED', {columns, reload: true});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setSortingColumn
                 * @description Sets the sorting on a column
                 * @param   {Object|alias} iColumn The column configuration or alias
                 * @param   {string=} sortDirection The sort direction
                 * @param   {boolean=} resetSorting Reset the current columns sorting flag
                 * @returns {WtAnalysisColumns}
                 */
                setSortingColumn(iColumn, sortDirection = SORT_DESCENDING, resetSorting = true) {
                    const column = this.getColumn(iColumn);

                    if (!column) {
                        throw Error('Failed to set the column sorting due to invalid column identifier');
                    }

                    if (!_.includes([SORT_ASCENDING, SORT_DESCENDING], sortDirection)) {
                        throw Error('Failed to set the column sorting due to invalid sorting direction');
                    }

                    if (resetSorting) {
                        this.resetSorting_(false);
                    }

                    column.sortDirection = sortDirection;

                    this.updateColumn(column, this.reloadOnSort_);

                    return this.dispatchAction('SORTING_UPDATED', {column});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getSessionLimits
                 * @description Retrieves the session limits for an analysis variant for the first dimension.
                 * When there is no limit for the requested variant, it is computed based on the current analysis configuration and the
                 * requested variant.
                 * @param {string=} iVariant The analysis variant for which to retrieve the limits. Defaults to current analysis variant.
                 * @returns {Array} The limits of the first dimension based on the variant [lowerLimit, upperLimit]
                 */
                getSessionLimits(iVariant = '') {
                    if (!this.hasDimension) {
                        return [1, 1];
                    }

                    const variant = iVariant ? iVariant : this.analysis.get('variant');

                    // The session limits are already computed and stored for the requested variant
                    if (this.sessionLimits_[variant]) {
                        return this.sessionLimits_[variant];
                    }

                    // There is no session limit stored in the cache for the requested variant, calculate the possible limits.
                    // Note that for the current analysis variant the limits are already computed, hence below code should compute
                    // the limits for a potential variant of the current analysis
                    const analysisConfig = _.cloneDeep(this.analysis.config);
                    analysisConfig.variant = variant;

                    const lowerLimit = 1;
                    const upperLimit = (() => {
                        const firstDimension = this.getFirstDimension();
                        const timeFormat = _.get(firstDimension, 'timeFormat');
                        const analysisFilters = this.analysis.get('predefinedContainer.filters');
                        const timeFilter = Filters.hasTimeFilter(
                            _.isEmpty(analysisFilters) ? [this.analysis.getSessionTimeFilter()] : analysisFilters
                        );

                        if (timeFormat && timeFilter && variant !== 'PIVOT_AS_LIST') {
                            // Not all time formats are handled by the query builder (eg. day_hyphen|month_hyphen for cohorts)
                            let upperLimitByTimeFormat = QueryLimits.getUpperLimitByTimeFormat(timeFormat, timeFilter);

                            if (upperLimitByTimeFormat) {
                                return upperLimitByTimeFormat;
                            }
                        }

                        // Read the upper limit from the dimension column configuration on initialization when present
                        const initLoad = _.isEmpty(this.sessionLimits_);
                        const presetUpperLimit = _.get(firstDimension, 'upperLimit');

                        if (initLoad && presetUpperLimit) {
                            return presetUpperLimit;
                        }

                        return QueryLimits.getDefaultUpperLimitByVariant(variant);
                    })();

                    return [lowerLimit, upperLimit];
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setSessionLimits
                 * @description Updates the session limits as well as the limits on the first dimension configuration.
                 * @param {string} variant The analysis variant
                 * @param {number} lowerLimit The lower limit
                 * @param {number} upperLimit The upper limit
                 * @param {boolean=} reload Does the session limit change require a new analysis data request
                 * @returns {WtAnalysisColumns}
                 */
                setSessionLimits(variant, lowerLimit, upperLimit, reload = true) {
                    if (!this.hasDimension) {
                        return this;
                    }

                    const column = this.getFirstDimension();

                    if (!_.includes(_.values(this.WtAnalysis.VARIANTS), variant)) {
                        throw Error(`Failed to set session limits due to an invalid variant "${variant}"`);
                    }

                    if (!_.isNumber(lowerLimit) || !_.isNumber(upperLimit) || lowerLimit < 1 || upperLimit < lowerLimit) {
                        throw Error(`Failed to set session limits due to invalid limit values "${lowerLimit}" "${upperLimit}"`);
                    }

                    // Do not trigger column update when not necessary
                    if (column.lowerLimit === lowerLimit && column.upperLimit === upperLimit) {
                        return this;
                    }

                    this.cacheSessionLimits_(variant, lowerLimit, upperLimit);

                    column.lowerLimit = lowerLimit;
                    column.upperLimit = upperLimit;

                    this.updateColumn(column, reload);

                    return this.dispatchAction('SESSION_LIMIT_UPDATED', {column, lowerLimit, upperLimit});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#stepUpSessionLimits
                 * @description Updates the session limits by increasing the current upperLimit by a given step, making sure not to
                 * exceed the max row limit.
                 * @param  {number} step
                 * @param  {string=} variant_
                 * @returns {WtAnalysisColumns}
                 */
                stepUpSessionLimits(step, variant_ = '') {
                    const variant = variant_ ? variant_ : this.analysis.get('variant');

                    const limits = this.getSessionLimits(variant);
                    const lowerLimit = limits[0];
                    const upperLimit = limits[1];

                    const desiredUpperLimit = upperLimit - lowerLimit + 1 + step;

                    const newUpperLimit = lowerLimit + _.min([this.maxRows, desiredUpperLimit]) - 1;

                    return this.setSessionLimits(variant, lowerLimit, newUpperLimit);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getColumnLimits
                 * @description Retrieves the limits for a dimension column. When no limits are configured on the dimension column,
                 * the limits will fallback to the default limits based on the current analysis variant
                 * @param {Object|string} iColumn The column alias or the column configuration
                 * @returns {Array} The limits in the form of [lowerLimit, upperLimit]
                 */
                getColumnLimits(iColumn) {
                    const defaultLowerLimit = 1;
                    const defaultUpperLimit = QueryLimits.getDefaultUpperLimitByVariant(this.analysis.get('variant'));

                    if (!this.hasDimension) {
                        return [defaultLowerLimit, defaultUpperLimit];
                    }

                    const column = this.getColumn(iColumn);
                    const firstDimension = this.getFirstDimension();

                    if (!column) {
                        throw Error('Failed to retrieve the limits as the column does not exist');
                    }

                    if (!column.dimension) {
                        throw Error('The requested column type does not support limits');
                    }

                    // First dimension has special logic of computing the limits (it is based on the variant and whether it is a time dimension)
                    if (firstDimension.alias === column.alias) {
                        return this.getSessionLimits();
                    }

                    // All other dimensions except first dimension
                    const lowerLimit = column.lowerLimit || defaultLowerLimit;
                    const upperLimit = column.upperLimit || defaultUpperLimit;

                    return [lowerLimit, upperLimit];
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#setColumnLimits
                 * @description Sets a dimension limits. If the requested dimension is the first dimension, the session limits will be set.
                 * @param {Object|string} iColumn The column alias or the column configuration
                 * @param {number} lowerLimit The lower limit
                 * @param {number} upperLimit The upper limit
                 * @param {boolean=} reload Does the column limits change trigger a new request
                 * @returns {WtAnalysisColumns}
                 */
                setColumnLimits(iColumn, lowerLimit, upperLimit, reload = true) {
                    if (!this.hasDimension) {
                        return this;
                    }

                    const column = this.getColumn(iColumn);
                    const firstDimension = this.getFirstDimension();

                    if (!column) {
                        throw Error(`Failed to set the limits as the column "${iColumn}" does not exist`);
                    }

                    if (!column.dimension) {
                        throw Error('The requested column type does not support limits');
                    }

                    if (!_.isNumber(lowerLimit) || !_.isNumber(upperLimit) || lowerLimit < 1 || upperLimit < lowerLimit) {
                        throw Error(`Failed to set the limits due to invalid limit values "${lowerLimit}" "${upperLimit}"`);
                    }

                    // Do not trigger column update when not necessary
                    if (column.lowerLimit === lowerLimit && column.upperLimit === upperLimit) {
                        return this;
                    }

                    if (firstDimension.alias === column.alias) {
                        // Sets the limits of the first dimension via the session limits
                        this.setSessionLimits(this.analysis.get('variant'), lowerLimit, upperLimit, reload);
                    } else {
                        // All other dimensions except first dimension
                        column.lowerLimit = lowerLimit;
                        column.upperLimit = upperLimit;

                        return this.updateColumn(column, reload);
                    }

                    return this.dispatchAction('COLUMN_LIMIT_UPDATED', {reload, column, lowerLimit, upperLimit});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getColumnTime
                 * @description Will return the time filter associated with one column
                 * @param {Object|string} column_ The column alias or the column configuration
                 * @returns {Object}
                 */
                getColumnTime(column_) {
                    const layer = this.analysis_.layer;
                    const column = this.getColumn(column_);
                    const configTime = ConfigPeriodColumn
                        .initPeriod(column, layer)
                        .getConfigTimeFilter(column, layer, true);
                    const analysisTime = this.analysis_.getSessionTimeFilter();
                    return configTime || analysisTime;
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#isMetric
                 * @description Will determine if a column is a metric column or not
                 * @param {Object|string} column_ The column alias or the column configuration
                 * @returns {boolean}
                 */
                isMetric(column_) {
                    const column = this.getColumn(column_);
                    const arrangeType = _.get(column, 'arrangeType');
                    const monad = Monads.layer(this.analysis.layer).monad(arrangeType);
                    return _.get(monad, 'metric', false);
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#isSparkline
                 * @description Will detect if a column is to be a sparkline or not
                 * @param {Object|string} column_ The column alias or the column configuration
                 * @returns {boolean}
                 */
                isSparkline(column_) {
                    if (!this.analysis.get('realTime')) {
                        return false;
                    }

                    if (!this.isMetric(column_)) {
                        return false;
                    }

                    const column = this.getColumn(column_);

                    if (_.get(column, 'dataVisual') !== 'SPARKLINE') {
                        return false;
                    }

                    return _.chain(column)
                        .get('sumValue')
                        .isEqual(false)
                        .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#isRank
                 * @description Will detect if a column is to be a rank or not
                 * @param {Object|string} column_ The column alias or the column configuration
                 * @returns {boolean}
                 */
                isRank(column_) {
                    if (!this.analysis.get('realTime')) {
                        return false;
                    }

                    if (!this.isMetric(column_)) {
                        return false;
                    }

                    const column = this.getColumn(column_);

                    if (_.get(column, 'dataVisual') !== 'RANK') {
                        return false;
                    }

                    return _.chain(this.getColumn(column))
                        .get('sumValue')
                        .isEqual(false)
                        .value();
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getChartVisibleColumns
                 * @description Returns the metric columns displayed in the chart
                 * @returns {Array}
                 */
                getChartVisibleColumns() {
                    return _.filter(this.getMetrics(), {
                        chart: {
                            visible: true
                        }
                    });
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#getTimeSeriesChartVisibleColumns
                 * @description Returns the metric columns toggled in time series and displayed in the chart
                 * @returns {Array}
                 */
                getTimeSeriesChartVisibleColumns() {
                    return _.filter(this.getChartVisibleColumns(), {inTimeSeries: true});
                }

                /**
                 * @ngdoc method
                 * @name WtAnalysisColumns#resetSorting_
                 * @description Removes the sorting information from all columns configuration
                 * @param {boolean=} reload Should the analysis reload the data
                 * @returns {WtAnalysisColumns}
                 */
                resetSorting_(reload = false) {
                    const columns = _.cloneDeep(this.config);

                    _.forEach(columns, (column) => {
                        delete column.sortDirection;
                    });

                    return this.updateData_(columns, reload);
                }
            };
        }]);
})(window.angular, window._);
